import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile } from "react-icons/fi";
import { GET_SALES_REPORT_WITH_ITEMS, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HighlightMetricsModal from './components/HighlightMetricsModal';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

const isAxiosAbort = (error) => {
    // Axios v1 uses AbortController and throws CanceledError with code ERR_CANCELED
    return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
};

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatInvoiceForPrint = (invoiceNo) => {
    const rawString = String(invoiceNo ?? '').trim();
    if (!rawString) return '';
    if (rawString.includes('/')) return rawString;

    const parsedInvoice = Number.parseInt(rawString, 10);
    const invoicePart = Number.isNaN(parsedInvoice)
        ? rawString
        : String(parsedInvoice).padStart(2, '0');
    const seriesName = String(localStorage.getItem('series_name') || '').trim();

    return seriesName ? `${invoicePart}/${seriesName}` : invoicePart;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],
  table: {
    columns: [
      { key: 'Sl', label: 'Sl.' },
      { key: 'BillNo', label: 'Bill No' },
      { key: 'BillDate', label: 'Bill Date' },
      { key: 'Soldto', label: 'Soldto' },
      { key: 'ProductName', label: 'Product Name' },
      { key: 'HSNCode', label: 'HSN Code' },
      { key: 'Batch', label: 'Batch' },
      { key: 'Nos', label: 'Nos' },
      { key: 'Rate', label: 'Rate' },
      { key: 'Taxable Amount', label: 'Taxable Amount' },
      { key: 'TaxPercent', label: 'Tax %' },
      { key: 'Tax', label: 'Tax' },
      { key: 'CGST', label: 'CGST' },
      { key: 'SGST', label: 'SGST' },
      { key: 'Total Mrp', label: 'Total Mrp' },
      { key: 'DisPercent', label: 'Dis %' },
      { key: 'Discount', label: 'Discount' },
      { key: 'GTotal', label: 'G.Total' },
    ],
  },
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Export Progress Modal (blocks outside click; cancel only via button)
const ExportProgressModal = ({ isOpen, title, message, progressPct, progressText, onCancel, isCancelling }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div
                className="bg-white rounded-2xl shadow-2xl w-[28rem] max-w-[90vw]"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title || 'Export progress'}
            >
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> {title || 'Exporting...'}
                    </h2>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-700 mb-4">{message || 'Preparing export...'}</p>
                    <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-indigo-600 transition-all duration-200"
                            style={{ width: `${Math.min(100, Math.max(0, progressPct || 0))}%` }}
                        />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <span>{progressText || ''}</span>
                        <span>{Math.round(progressPct || 0)}%</span>
                    </div>
                    <div className="mt-5 flex justify-end">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isCancelling}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-red-300 hover:text-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {isCancelling ? 'Cancelling...' : 'Cancel Export'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesLedger = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const token = localStorage.getItem('accessToken');
    const [userInfo, setUserInfo] = useState({});
    const [showExportModal, setShowExportModal] = useState(false);
    const exportAbortControllerRef = useRef(null);
    const [exportProgress, setExportProgress] = useState({
        isOpen: false,
        title: '',
        message: '',
        progressPct: 0,
        progressText: '',
        isCancelling: false,
    });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(500);
    const [pageContext, setPageContext] = useState({
        PageNo: 1,
        PageSize: 500,
        TotalRecords: 0,
        TotalPages: 0,
        HasNextPage: false,
        HasPreviousPage: false
    });

    const isElectron = () => {
        return window && window.process && window.process.type;
    };
    
    let ipcRenderer;

    if (isElectron()) {
        ipcRenderer = window.require('electron').ipcRenderer;
    }

    useEffect(() => {
        const fetchData = async () => {
            let userinfo;

            if (isElectron()) {
                userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
                setUserInfo(userinfo);
            } else {
                initializeWebUserInfo();
                userinfo = getWebUserInfo().userInfo;
                setUserInfo(userinfo);
            }
        };

        fetchData();
    }, []);

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getSalesReport();
    };

    const handleExport = () => {
        // Check if filters are set
        if (!filters.fromDate || !filters.toDate) {
            toast.error('Please select date range before exporting', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const openExportProgress = (title) => {
        setExportProgress({
            isOpen: true,
            title: title || 'Exporting...',
            message: 'Starting export...',
            progressPct: 0,
            progressText: '',
            isCancelling: false,
        });
    };

    const closeExportProgress = () => {
        setExportProgress((prev) => ({ ...prev, isOpen: false, isCancelling: false }));
    };

    const updateExportProgress = ({ message, completedPages, totalPages }) => {
        const safeTotal = Math.max(1, totalPages || 1);
        const safeCompleted = Math.min(safeTotal, Math.max(0, completedPages || 0));
        const pct = (safeCompleted / safeTotal) * 100;
        setExportProgress((prev) => ({
            ...prev,
            message: message ?? prev.message,
            progressPct: pct,
            progressText: `Fetched ${safeCompleted} / ${safeTotal} pages`,
        }));
    };

    const cancelExport = () => {
        setExportProgress((prev) => ({ ...prev, isCancelling: true, message: 'Cancelling export...' }));
        try {
            exportAbortControllerRef.current?.abort();
        } catch (e) {
            // ignore
        }
    };

    const exportToExcel = async () => {
        setLoading(true);
        openExportProgress('Exporting to Excel');
        try {
            // Create fresh controller for this export
            exportAbortControllerRef.current = new AbortController();
            const { signal } = exportAbortControllerRef.current;

            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            toast.info('Fetching all data for export...', { autoClose: 2000 });
            updateExportProgress({ message: 'Fetching page 1...', completedPages: 0, totalPages: 1 });
            
            // First, fetch page 1 to get total pages
            const firstPageResponse = await axios.get(`${BASE_URL}${GET_SALES_REPORT_WITH_ITEMS}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'pageNo': 1,
                    'pageSize': 500, // Maximum page size
                },
                signal,
            });

            if (firstPageResponse.status !== 200 || !firstPageResponse.data.isSucess) {
                toast.error('Failed to fetch data for export', { autoClose: 2000 });
                setLoading(false);
                closeExportProgress();
                return;
            }

            const pageContextData = firstPageResponse.data.data?.pageContext || {};
            const totalPages = pageContextData.TotalPages || 1;
            let allData = firstPageResponse.data.data?.salesData || [];
            updateExportProgress({ message: 'Fetched page 1', completedPages: 1, totalPages });

            // Fetch remaining pages if there are more than 1 page
            if (totalPages > 1) {
                toast.info(`Fetching ${totalPages} pages of data...`, { autoClose: 2000 });
                
                let completedPages = 1; // already fetched page 1
                const pagePromises = [];
                for (let pageNo = 2; pageNo <= totalPages; pageNo++) {
                    const req = axios.get(`${BASE_URL}${GET_SALES_REPORT_WITH_ITEMS}`, {
                        headers: {
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            'Authorization': `Bearer ${token}`,
                        },
                        params: {
                            'fromDate': formatDate(fromDate),
                            'toDate': formatDate(toDate),
                            'pageNo': pageNo,
                            'pageSize': 500,
                        },
                        signal,
                    })
                        .then((response) => {
                            if (response.status === 200 && response.data.isSucess) {
                                const pageData = response.data.data?.salesData || [];
                                allData = [...allData, ...pageData];
                            }
                        })
                        .finally(() => {
                            completedPages += 1;
                            updateExportProgress({
                                message: `Fetching pages... (${completedPages}/${totalPages})`,
                                completedPages,
                                totalPages,
                            });
                        });

                    pagePromises.push(req);
                }

                // Fetch all pages in parallel (progress updates as each finishes)
                const results = await Promise.allSettled(pagePromises);
                const rejected = results.find(r => r.status === 'rejected');
                if (rejected) {
                    // If cancelled, treat gracefully; otherwise throw the first error
                    const err = rejected.reason;
                    if (isAxiosAbort(err)) {
                        toast.info('Export cancelled', { autoClose: 2000 });
                        setLoading(false);
                        closeExportProgress();
                        return;
                    }
                    throw err;
                }
            }
            
            // Filter only Sales documents (exclude Sales - Return and others)
            allData = allData.filter(item => item.DocType === "Sales");
            setExportProgress((prev) => ({ ...prev, message: 'Preparing Excel file...' }));
            
            // Transform all data
            let previousBillNo = null;
            const transformedData = allData.map((item, index) => {
                const isFirstItem = previousBillNo !== item.InvoiceNo;
                previousBillNo = item.InvoiceNo;
                
                const igst = parseFloat(item.IGST || 0);
                const cess = parseFloat(item.Cess || 0);
                
                return {
                    BillNo: isFirstItem ? item.InvoiceNo : '',
                    // Excel export requirement: show Bill Date for ALL items (not just first line per bill)
                    BillDate: item.CreatedDate || '',
                    Doctor: '',
                    Soldto: isFirstItem ? item.PatientName : '',
                    ProductName: item.ItemName,
                    HSNCode: item.HsnCode || item.HSNCode || '',
                    Batch: item.BatchCode,
                    Expiry: '',
                    Nos: item.Qty,
                    Rate: item.MRP,
                    Free: item.FOC,
                    TaxPercent: item.TaxCode,
                    TRate: item.TaxAmount,
                    GTotal: item.Total,
                    Tax: item.TaxAmount,
                    CGST: item.CGST,
                    SGST: item.SGST,
                    IGST: igst,
                    Cess: cess,
                    Amount: item.SalesPrice,
                    MRP: item.Total,
                    DisPercent: item.Disc_Percentage,
                    Discount: item.Disc_Amount,
                    Taxablevalue: item.Taxablevalue || 0,
                };
            });

            const excelData = transformedData.map((row, index) => {
                // Calculate Rate as Taxablevalue / Nos (matching table display)
                const rate = row.Nos && parseFloat(row.Nos) !== 0 
                    ? parseFloat(row.Taxablevalue || 0) / parseFloat(row.Nos || 1)
                    : 0;
                
                // Format Bill Date with date and time
                let billDateFormatted = '';
                if (row.BillDate && row.BillDate.trim() !== '') {
                    const date = new Date(row.BillDate);
                    if (!isNaN(date.getTime())) {
                        billDateFormatted = `${date.toLocaleDateString('en-GB')}`;
                    }
                }
                
                return {
                    'Sl.': index + 1,
                    'Bill No': row.BillNo ? formatInvoiceForPrint(row.BillNo) : '',
                    'Bill Date': billDateFormatted,
                    'Soldto': row.Soldto || '',
                    'Product Name': row.ProductName || '',
                    'HSN Code': row.HSNCode || '',
                    'Batch': row.Batch || '',
                    'Nos': Number(parseFloat(row.Nos || 0).toFixed(3)),
                    'Rate': Number(parseFloat(rate || 0).toFixed(2)),
                    'Taxable Amount': Number(parseFloat(row.Taxablevalue || 0).toFixed(2)),
                    'Tax %': Number(parseFloat(row.TaxPercent || 0).toFixed(2)),
                    'Tax': Number(parseFloat(row.TRate || 0).toFixed(2)),
                    'CGST': Number(parseFloat(row.CGST || 0).toFixed(2)),
                    'SGST': Number(parseFloat(row.SGST || 0).toFixed(2)),
                    'Total Mrp': Number(parseFloat(row.MRP || 0).toFixed(2)),
                    'Dis %': Number(parseFloat(row.DisPercent || 0).toFixed(2)),
                    'Discount': Number(parseFloat(row.Discount || 0).toFixed(2)),
                    'G.Total': Number(parseFloat(row.Amount * row.Nos || 0).toFixed(2)),
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = [
                { wch: 6 },   // Sl.
                { wch: 10 },  // Bill No
                { wch: 18 },  // Bill Date (with time)
                { wch: 20 },  // Soldto
                { wch: 30 },  // Product Name
                { wch: 12 },  // HSN Code
                { wch: 12 },  // Batch
                { wch: 10 },  // Nos
                { wch: 10 },  // Rate
                { wch: 14 },  // Taxable Amount
                { wch: 8 },   // Tax %
                { wch: 10 },  // Tax
                { wch: 10 },  // CGST
                { wch: 10 },  // SGST
                { wch: 12 },  // Total Mrp
                { wch: 8 },   // Dis %
                { wch: 12 },  // Discount
                { wch: 12 },  // G.Total
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Sales Ledger');

            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Sales_Ledger_${fromDateStr}_to_${toDateStr}.xlsx`;

            XLSX.writeFile(wb, filename);
            toast.success(`Excel file exported successfully with ${transformedData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            if (isAxiosAbort(error)) {
                toast.info('Export cancelled', { autoClose: 2000 });
            } else {
                toast.error('Failed to export to Excel', { autoClose: 2000 });
            }
        } finally {
            setLoading(false);
            closeExportProgress();
            exportAbortControllerRef.current = null;
        }
    };

    const exportToPDF = async () => {
        setLoading(true);
        openExportProgress('Exporting to PDF');
        try {
            exportAbortControllerRef.current = new AbortController();
            const { signal } = exportAbortControllerRef.current;

            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            toast.info('Fetching all data for export...', { autoClose: 2000 });
            updateExportProgress({ message: 'Fetching page 1...', completedPages: 0, totalPages: 1 });
            
            // First, fetch page 1 to get total pages
            const firstPageResponse = await axios.get(`${BASE_URL}${GET_SALES_REPORT_WITH_ITEMS}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'pageNo': 1,
                    'pageSize': 500, // Maximum page size
                },
                signal,
            });

            if (firstPageResponse.status !== 200 || !firstPageResponse.data.isSucess) {
                toast.error('Failed to fetch data for export', { autoClose: 2000 });
                setLoading(false);
                closeExportProgress();
                return;
            }

            const pageContextData = firstPageResponse.data.data?.pageContext || {};
            const totalPages = pageContextData.TotalPages || 1;
            let allData = firstPageResponse.data.data?.salesData || [];
            updateExportProgress({ message: 'Fetched page 1', completedPages: 1, totalPages });

            // Fetch remaining pages if there are more than 1 page
            if (totalPages > 1) {
                toast.info(`Fetching ${totalPages} pages of data...`, { autoClose: 2000 });
                
                let completedPages = 1;
                const pagePromises = [];
                for (let pageNo = 2; pageNo <= totalPages; pageNo++) {
                    const req = axios.get(`${BASE_URL}${GET_SALES_REPORT_WITH_ITEMS}`, {
                        headers: {
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            'Authorization': `Bearer ${token}`,
                        },
                        params: {
                            'fromDate': formatDate(fromDate),
                            'toDate': formatDate(toDate),
                            'pageNo': pageNo,
                            'pageSize': 500,
                        },
                        signal,
                    })
                        .then((response) => {
                            if (response.status === 200 && response.data.isSucess) {
                                const pageData = response.data.data?.salesData || [];
                                allData = [...allData, ...pageData];
                            }
                        })
                        .finally(() => {
                            completedPages += 1;
                            updateExportProgress({
                                message: `Fetching pages... (${completedPages}/${totalPages})`,
                                completedPages,
                                totalPages,
                            });
                        });

                    pagePromises.push(req);
                }

                const results = await Promise.allSettled(pagePromises);
                const rejected = results.find(r => r.status === 'rejected');
                if (rejected) {
                    const err = rejected.reason;
                    if (isAxiosAbort(err)) {
                        toast.info('Export cancelled', { autoClose: 2000 });
                        setLoading(false);
                        closeExportProgress();
                        return;
                    }
                    throw err;
                }
            }
            
            // Filter only Sales documents (exclude Sales - Return and others)
            allData = allData.filter(item => item.DocType === "Sales");
            setExportProgress((prev) => ({ ...prev, message: 'Preparing PDF file...' }));
            
            // Transform all data
            let previousBillNo = null;
            const transformedData = allData.map((item, index) => {
                const isFirstItem = previousBillNo !== item.InvoiceNo;
                previousBillNo = item.InvoiceNo;
                
                const igst = parseFloat(item.IGST || 0);
                const cess = parseFloat(item.Cess || 0);
                
                return {
                    BillNo: isFirstItem ? item.InvoiceNo : '',
                    BillDate: isFirstItem ? item.CreatedDate : '',
                    Doctor: '',
                    Soldto: isFirstItem ? item.PatientName : '',
                    ProductName: item.ItemName,
                    HSNCode: item.HsnCode || item.HSNCode || '',
                    Batch: item.BatchCode,
                    Expiry: '',
                    Nos: item.Qty,
                    Rate: item.MRP,
                    Free: item.FOC,
                    TaxPercent: item.TaxCode,
                    TRate: item.TaxAmount,
                    GTotal: item.Total,
                    Tax: item.TaxAmount,
                    CGST: item.CGST,
                    SGST: item.SGST,
                    IGST: igst,
                    Cess: cess,
                    Amount: item.SalesPrice,
                    MRP: item.Total,
                    DisPercent: item.Disc_Percentage,
                    Discount: item.Disc_Amount,
                    Taxablevalue: item.Taxablevalue || 0,
                };
            });

            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            doc.setFontSize(16);
            doc.text('Sales Ledger Report', 14, 15);
            
            doc.setFontSize(10);
            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDateStr} to ${toDateStr}`, 14, 22);
            
            const tableData = transformedData.map((row, index) => {
                // Calculate Rate as Taxablevalue / Nos (matching table display)
                const rate = row.Nos && parseFloat(row.Nos) !== 0 
                    ? parseFloat(row.Taxablevalue || 0) / parseFloat(row.Nos || 1)
                    : 0;
                
                // Format Bill Date with date and time
                let billDateFormatted = '';
                if (row.BillDate && row.BillDate.trim() !== '') {
                    const date = new Date(row.BillDate);
                    if (!isNaN(date.getTime())) {
                        billDateFormatted = `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                    }
                }
                
                return [
                    index + 1,
                    row.BillNo ? formatInvoiceForPrint(row.BillNo) : '',
                    billDateFormatted,
                    row.Soldto || '',
                    row.ProductName || '',
                    row.HSNCode || '',
                    row.Batch || '',
                    parseFloat(row.Nos || 0).toFixed(3),
                    parseFloat(rate || 0).toFixed(2),
                    parseFloat(row.Taxablevalue || 0).toFixed(2),
                    parseFloat(row.TaxPercent || 0).toFixed(2),
                    parseFloat(row.TRate || 0).toFixed(2),
                    parseFloat(row.CGST || 0).toFixed(2),
                    parseFloat(row.SGST || 0).toFixed(2),
                    parseFloat(row.MRP || 0).toFixed(2),
                    parseFloat(row.DisPercent || 0).toFixed(2),
                    parseFloat(row.Discount || 0).toFixed(2),
                    parseFloat(row.Amount * row.Nos || 0).toFixed(2),
                ];
            });

            autoTable(doc, {
                head: [['Sl.', 'Bill No', 'Bill Date', 'Soldto', 'Product Name', 'HSN Code', 'Batch', 'Nos', 'Rate', 'Taxable Amount', 'Tax %', 'Tax', 'CGST', 'SGST', 'Total Mrp', 'Dis %', 'Discount', 'G.Total']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 6, cellPadding: 1 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
            });

            const filename = `Sales_Ledger_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
            doc.save(filename);
            toast.success(`PDF file exported successfully with ${transformedData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            if (isAxiosAbort(error)) {
                toast.info('Export cancelled', { autoClose: 2000 });
            } else {
                toast.error('Failed to export to PDF', { autoClose: 2000 });
            }
        } finally {
            setLoading(false);
            closeExportProgress();
            exportAbortControllerRef.current = null;
        }
    };

    // Pagination calculations - using backend pageContext
    const totalPages = pageContext.TotalPages || 0;
    const totalRecords = pageContext.TotalRecords || 0;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + salesData.length, totalRecords);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate]);

    // Fetch data when page or page size changes - always fetch from backend
    useEffect(() => {
        // Only fetch if filters are set (to avoid fetching on initial mount before filters are ready)
        if (filters.fromDate && filters.toDate) {
            getSalesReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage]);

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    const getSalesReport = useCallback(async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT_WITH_ITEMS}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'pageNo': currentPage,
                    'pageSize': itemsPerPage,
                },
            });

            
            if (response.status === 200 && response.data.isSucess) {
                const data = response.data.data?.salesData || [];
                const pageContextData = response.data.data?.pageContext || {};
                
                // Filter only Sales documents (exclude Sales - Return and others)
                const filteredData = data.filter(item => item.DocType === "Sales");
                // Transform API data to match table structure
                let previousBillNo = null;
                const transformedData = filteredData.map((item, index) => {
                    // Check if this is the first item for this bill
                    const isFirstItem = previousBillNo !== item.InvoiceNo;
                    previousBillNo = item.InvoiceNo;
                    
                    // Calculate IGST (if applicable, otherwise 0)
                    const igst = parseFloat(item.IGST || 0);
                    // Calculate Cess (if applicable, otherwise 0)
                    const cess = parseFloat(item.Cess || 0);
                    
                return {
                    BillNo: isFirstItem ? item.InvoiceNo : '',
                    BillDate: isFirstItem ? item.CreatedDate : '',
                    Doctor: '', // Not in API response
                    Soldto: isFirstItem ? item.PatientName : '',
                    ProductName: item.ItemName,
                    HSNCode: item.HsnCode || item.HSNCode || '',
                    Batch: item.BatchCode,
                    Expiry: '', // Not in API response
                    Nos: item.Qty,
                    Rate: item.MRP,
                    Free: item.FOC,
                    TaxPercent: item.TaxCode,
                    TRate: item.TaxAmount,
                    GTotal: item.Total,
                    Tax: item.TaxAmount,
                    CGST: item.CGST,
                    SGST: item.SGST,
                    IGST: igst,
                    Cess: cess,
                    Amount: item.SalesPrice,
                    MRP: item.Total,
                    DisPercent: item.Disc_Percentage,
                    Discount: item.Disc_Amount,
                    Taxablevalue: item.Taxablevalue || 0,
                };
                });
                
                setSalesData(transformedData);
                setPageContext({
                    PageNo: pageContextData.PageNo || currentPage,
                    PageSize: pageContextData.PageSize || itemsPerPage,
                    TotalRecords: pageContextData.TotalRecords || 0,
                    TotalPages: pageContextData.TotalPages || 0,
                    HasNextPage: pageContextData.HasNextPage === 1 || pageContextData.HasNextPage === true,
                    HasPreviousPage: pageContextData.HasPreviousPage === 1 || pageContextData.HasPreviousPage === true
                });
                setLastRefreshedAt(new Date());
            } else {
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch sales report:', error);
            toast.error('Failed to fetch sales report', { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    }, [filters, token, currentPage, itemsPerPage]);

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <ExportProgressModal
                isOpen={exportProgress.isOpen}
                title={exportProgress.title}
                message={exportProgress.message}
                progressPct={exportProgress.progressPct}
                progressText={exportProgress.progressText}
                isCancelling={exportProgress.isCancelling}
                onCancel={cancelExport}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:items-center lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
                        {!embedded && (
                            <div className="min-w-0 shrink-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Sales Report
                                </p>
                                <h1 className="text-lg font-bold text-slate-800">
                                    Sales Ledger
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            {reportConfig.filters.map((filter) => {
                                if (filter.type === 'date') {
                                    return (
                                        <label key={filter.id} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                            <span className="whitespace-nowrap">{filter.label}:</span>
                                            <input
                                                type="date"
                                                value={filters[filter.id] ?? ''}
                                                onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                            />
                                        </label>
                                    );
                                }
                                return null;
                            })}
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-5">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Items
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{totalRecords}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.Amount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Discount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.Discount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Tax
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.Tax || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Grand Total
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.GTotal || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 pb-1">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{endIndex}</span> of <span className="font-semibold text-slate-700">{totalRecords}</span> items for this report.
                                    </p>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={!pageContext.HasPreviousPage || currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={!pageContext.HasNextPage || currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {salesData.length > 0 ? (
                                            salesData.map((row, index) => (
                                                <tr key={startIndex + index} className="hover:bg-indigo-50/30">
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{startIndex + index + 1}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.BillNo ? formatInvoiceForPrint(row.BillNo) : ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">
                                                        {(() => {
                                                            if (!row.BillDate || row.BillDate.trim() === '') return '';
                                                            const date = new Date(row.BillDate);
                                                            if (isNaN(date.getTime())) return '';
                                                            return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                                                        })()}
                                                    </td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.Soldto || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.ProductName || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.HSNCode || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.Batch || ''}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.Nos || 0).toFixed(3)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat( row.Taxablevalue / row.Nos).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-center">₹{parseFloat(row.Taxablevalue || 0 ).toFixed(2)}</td>



                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.TaxPercent || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.TRate || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.CGST || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.SGST || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.MRP || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.DisPercent || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Discount || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.Amount * row.Nos || 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-8 text-center text-slate-500">
                                                    No data available for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(SalesLedger);

